import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { ClickAwayListener } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import Icon from 'src/components/Icon/Icon';
import './SmartInput.scss';
import { RootState } from 'src/configs/store';

interface SmartInputProps {
  options: {code: string, name: string }[],
  onSelect: Function,
  placeHolder: string,
  onFocus: Function,
  className?: string,
  hasError?: boolean
  ref: any,
  name: string,
  defaultValue?: string,
  isFocused?:boolean,
  shouldOpen?:boolean
}

// eslint-disable-next-line react/display-name
const SmartInput = React.forwardRef(({
  options, onSelect, placeHolder, onFocus, className, hasError, name,
  defaultValue='', isFocused, shouldOpen
}: SmartInputProps, ref:any) => {
  const [value, setValue] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [newOptions, setNewOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(-1);
  const { typeOfCode } = useSelector((state: RootState)  => state.peripherals);
  const { valuesReset } = useSelector((state: RootState)  => state.lotto);
  const dispatch = useDispatch();

  useEffect(() => {
    if (defaultValue){
      setValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    setValue('');
    setShowOptions(false);
    dispatch.lotto.activateResetNumbers();
  }, [typeOfCode, valuesReset]);

  const tabFunction = (e) => {

    if (e.key === '/' && ref.current.className.includes('betcodes')){
      e.preventDefault();
      setValue('');
    }

    if (e.key === 'Tab' || e.key === 'ArrowRight' || e.key === 'ArrowLeft'){
      setShowOptions(false);
      if (selectedItem > -1){
        const selectedOption = newOptions[selectedItem];
        setSelectedItem(-1);
        setValue(`${ selectedOption.code } - ${ selectedOption.name }`);
        onSelect(selectedOption.code, name);
      }
    } else if (e.key === 'ArrowDown' && shouldOpen){
      if (showOptions){
        if (newOptions.length > selectedItem +1){
          setSelectedItem(selectedItem+1);
        }
        return ;
      } else {
        setShowOptions(true);
      }
    } else if (e.key === 'ArrowUp' && shouldOpen){
      if (showOptions){
        if (selectedItem >= 1){
          setSelectedItem(selectedItem-1);
        }
        return ;
      } else {
        setShowOptions(true);
      }
    }
  };

  useEffect(() => {
    setNewOptions(options);
  }, [options]);

  useEffect(() => {
    if (isFocused){
      document.addEventListener('keydown', tabFunction);
    }
    return () => {
      document.removeEventListener('keydown', tabFunction);
    };
  }, [value, options, selectedItem, ref, showOptions]);

  useEffect(() => {
    if (selectedItem > -1 && selectedItem < newOptions.length) {
      const itemList = document.getElementsByClassName('bank-transfer__list-item--selected');
      if (itemList && itemList[0]){
        itemList[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedItem, newOptions]);

  const handleSelectionChange = (e) => {
    e.preventDefault();
    setValue(e.target.value);

    onSelect(e.target.value, name);
  };

  const onClick = (code, newName) => {
    setValue(`${ code } - ${ newName }`);

    onSelect(code, name);
  };

  useEffect(() => {
    const filteredOptions = _.filter(options, (option) => {
      if (option.name.includes(value) || option.code.includes(value)){
        return option;
      }
      return null;
    });

    if (filteredOptions.length === 0){
      setShowOptions(false);
    } else if (filteredOptions.length > 0 && !showOptions && value.length < 8){
      setShowOptions(true);
    }

    setNewOptions(_.compact(filteredOptions));
  }, [value]);

  const handleFocus = () => {
    if (value.length > 0){
      setShowOptions(true);
      if (ref && ref.current){
        ref.current.select();
      }
    }
    onFocus();
  };

  return (
    <ClickAwayListener onClickAway={ () => setShowOptions(false) }>
      <div className="smart-input">
        <div className="bank-transfer__label-input">
          <div className="bank-transfer__search-container">
            <input
              type="text"
              name="bankAccount"
              onChange={ handleSelectionChange }
              placeholder={ placeHolder }
              ref={ ref }
              className={
                classNames({
                  ...(className && { [className]: true }),
                  ...(className && hasError && { [`${ className }__error`]: true })
                })
              }
              autoComplete="off"
              value={ value }
              onFocus={ handleFocus }
              onBlur={ () => setShowOptions(false) }
            />
            {
              newOptions &&
        <Icon
          icon="dropdownArrow"
          onClick= { () => setShowOptions(!showOptions) }
          className={
            classNames({
              'bank-transfer__select-icon': true,
              'bank-transfer__select-icon--open': showOptions
            })
          }
        />
            }
          </div>
          {
            showOptions && <div className="bank-transfer__bank-list">
              <ul className="bank-transfer__list-container">
                {
                  _.map(newOptions, (option, index) => {
                    return (
                      <li
                        key= { option.code }
                        className={
                          classNames({
                            'bank-transfer__list-item': true,
                            'bank-transfer__list-item--selected': +selectedItem === +index
                          })
                        }
                        onClick={ () => onClick(option.code, option.name) }
                      >
                        <span
                          className="bank-transfer__account-number"
                          onClick={ () => onClick(option.code, option.name) }
                        >{ `${ option.code } - ${ option.name }` }</span>
                      </li>);
                  })
                }
              </ul>
            </div>
          }
        </div>
      </div>
    </ClickAwayListener>
  );
});

export default SmartInput;
